/**
 * WS-NAV — the workspace switcher.
 *
 * The first element of the chrome, above the tree: it names where you are and
 * opens the flyout that takes you anywhere else. Per the approved mockup:
 * Home on top, one-line descriptions, ⌘/Ctrl+0–6 shortcut hints, and the
 * Platform workspace below a divider with its own indigo accent so
 * "cross-tenant staff tool" never reads like a normal tenant workspace.
 */
import { useState, type ReactElement } from "react";
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { typography, workspaceAccent } from "@aeriosiq/ui";
import { HOME_PATH } from "../workspaces/resolve.js";
import { useActiveWorkspace } from "../workspaces/useWorkspaces.js";
import { shortcutHint } from "./workspaceShortcuts.js";
import { CollapsedTooltip, IconCell, rowStyles } from "./primitives.js";
import type { Workspace } from "../workspaces/types.js";
import { ChevronsUpDownIcon, HouseIcon } from "@aeriosiq/ui/icons";

interface Props {
  collapsed: boolean;
  /** Fired after a destination is chosen — lets the mobile drawer close. */
  onNavigate?: (() => void) | undefined;
}

function accentFg(mode: "light" | "dark"): string {
  return mode === "dark" ? workspaceAccent.dark.fg : workspaceAccent.light.fg;
}

function WorkspaceMenuItem({
  workspace,
  onPick,
  isActive,
}: {
  workspace: Workspace;
  onPick: (w: Workspace) => void;
  isActive: boolean;
}): ReactElement {
  const isPlatform = workspace.accent === "platform";
  return (
    <MenuItem
      onClick={() => {
        onPick(workspace);
      }}
      selected={isActive}
      aria-label={`${workspace.label} — ${workspace.tagline}`}
      data-workspace-id={workspace.id}
      sx={{
        alignItems: "flex-start",
        py: 1,
        // Any tinted "selected" fill drags the 13 px tagline under WCAG AA
        // (MUI's own primary tint lands at 4.1 : 1, a neutral one at 4.3).
        // So the active row is marked by an inset accent bar + a semibold
        // label instead of a background wash — same signal, no contrast cost.
        "&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible":
          {
            bgcolor: "transparent",
            boxShadow: (t) =>
              `inset 3px 0 0 ${
                isPlatform ? accentFg(t.palette.mode) : t.palette.primary.main
              }`,
          },
      }}
    >
      <ListItemIcon
        sx={
          isPlatform
            ? { color: (t) => accentFg(t.palette.mode) }
            : { color: "text.secondary" }
        }
      >
        <workspace.Icon fontSize="small" />
      </ListItemIcon>
      <ListItemText
        primary={workspace.label}
        secondary={workspace.tagline}
        slotProps={{
          primary: {
            sx: {
              fontSize: typography.size["body-md"],
              fontWeight: isActive
                ? typography.weight.semibold
                : typography.weight.medium,
              ...(isPlatform
                ? {
                    color: (t: { palette: { mode: "light" | "dark" } }) =>
                      accentFg(t.palette.mode),
                  }
                : {}),
            },
          },
          secondary: { sx: { fontSize: typography.size["caption-lg"] } },
        }}
      />
      <Typography
        component="span"
        aria-hidden
        sx={{
          ml: 2,
          alignSelf: "center",
          color: "text.secondary",
          fontSize: typography.size["caption-sm"],
          whiteSpace: "nowrap",
        }}
      >
        {shortcutHint(workspace.shortcut)}
      </Typography>
    </MenuItem>
  );
}

export function WorkspaceSwitcher({
  collapsed,
  onNavigate,
}: Props): ReactElement | null {
  const { visible, workspace, navigateToWorkspace } = useActiveWorkspace();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);

  // Rendered even for a single-workspace caller: the flyout is also how a
  // pure pilot reaches Home, and the button labels where they are.
  if (visible.length === 0) return null;

  const tenantWorkspaces = visible.filter((w) => w.accent !== "platform");
  const platformWorkspaces = visible.filter((w) => w.accent === "platform");
  const activeLabel = workspace?.label ?? "Workspaces";
  const isPlatformActive = workspace?.accent === "platform";

  const close = (): void => {
    setAnchor(null);
  };
  const go = (path: string): void => {
    close();
    onNavigate?.();
    void navigate(path);
  };

  return (
    <Box sx={{ px: 1, pb: 0.5 }}>
      <CollapsedTooltip
        collapsed={collapsed}
        title={`${activeLabel} — switch workspace`}
      >
        <Box
          component="button"
          type="button"
          data-testid="workspace-switcher"
          onClick={(e) => {
            setAnchor(e.currentTarget);
          }}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Switch workspace, current: ${activeLabel}`}
          sx={[
            {
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontFamily: "inherit",
              textAlign: "left",
            },
            rowStyles({
              selected: false,
              collapsed,
              height: 44,
              accent: isPlatformActive ? "platform" : undefined,
            }),
          ]}
        >
          {workspace ? (
            <IconCell
              Icon={workspace.Icon}
              selected={false}
              accent={isPlatformActive ? "platform" : undefined}
            />
          ) : null}
          {collapsed ? null : (
            <>
              <Box
                component="span"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: typography.size["body-md"],
                  letterSpacing: typography.tracking["body-md"],
                  fontWeight: typography.weight.semibold,
                  color: isPlatformActive
                    ? (t) => accentFg(t.palette.mode)
                    : "text.primary",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {activeLabel}
              </Box>
              <ChevronsUpDownIcon
                fontSize="small"
                aria-hidden
                sx={{ color: "text.secondary", flexShrink: 0 }}
              />
            </>
          )}
        </Box>
      </CollapsedTooltip>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { minWidth: 300, maxWidth: 360 } } }}
        MenuListProps={{ "aria-label": "Workspaces" }}
      >
        <MenuItem
          onClick={() => {
            go(HOME_PATH);
          }}
          aria-label="Home — all workspaces"
          sx={{ alignItems: "flex-start", py: 1 }}
        >
          <ListItemIcon sx={{ color: "text.secondary" }}>
            <HouseIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Home"
            secondary="All workspaces at a glance"
            slotProps={{
              primary: {
                sx: {
                  fontSize: typography.size["body-md"],
                  fontWeight: typography.weight.medium,
                },
              },
              secondary: { sx: { fontSize: typography.size["caption-lg"] } },
            }}
          />
          <Typography
            component="span"
            aria-hidden
            sx={{
              ml: 2,
              alignSelf: "center",
              color: "text.secondary",
              fontSize: typography.size["caption-sm"],
              whiteSpace: "nowrap",
            }}
          >
            {shortcutHint(0)}
          </Typography>
        </MenuItem>
        <Divider />
        {tenantWorkspaces.map((w) => (
          <WorkspaceMenuItem
            key={w.id}
            workspace={w}
            isActive={w.id === workspace?.id}
            onPick={(picked) => {
              close();
              onNavigate?.();
              // The destination may be owned by more than one workspace, so
              // the choice travels with the navigation.
              navigateToWorkspace(picked);
            }}
          />
        ))}
        {platformWorkspaces.length > 0 ? <Divider /> : null}
        {platformWorkspaces.map((w) => (
          <WorkspaceMenuItem
            key={w.id}
            workspace={w}
            isActive={w.id === workspace?.id}
            onPick={(picked) => {
              close();
              onNavigate?.();
              // The destination may be owned by more than one workspace, so
              // the choice travels with the navigation.
              navigateToWorkspace(picked);
            }}
          />
        ))}
      </Menu>
    </Box>
  );
}
